import React, { createContext, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { format, addMinutes } from 'date-fns';

let previewUtcDate: string | undefined = undefined;

export const getPreviewDate = () => previewUtcDate;

interface IPreviewContext {
  date?: Date;
  dateString?: string;
  setDate: (date: Date) => void;
  utcDateString?: string;
}

export const PreviewContext = createContext<IPreviewContext | undefined>(undefined);

const getInitialDate = () => {
  if (typeof window === 'undefined') return new Date();

  if (!window.location.search.length) {
    return new Date();
  }
  const queryString = new URLSearchParams(window.location.search);

  if (!queryString.has('date')) {
    return new Date();
  }

  return new Date(queryString.get('date')?.replace(' ', '+') as string);
};

interface IPreviewProvider {
  children: ReactNode;
  isPreview: boolean;
}

const PreviewProvider = ({ children, isPreview }: IPreviewProvider) => {
  const initialDate = useRef(getInitialDate());
  const [date, setDate] = useState(initialDate.current);

  const dateString = useMemo(
    () => (isPreview ? format(date, `yyyy-MM-dd'T'HH:mm:ssxx`) : undefined),
    [date, isPreview],
  );

  previewUtcDate = useMemo(
    () =>
      isPreview
        ? `${format(addMinutes(date, date.getTimezoneOffset()), `yyyy-MM-dd'T'HH:mm:ss`)}Z`
        : undefined,
    [date, isPreview],
  );
  const utcDateString = previewUtcDate;

  useEffect(() => {
    if (date !== initialDate.current && utcDateString) {
      const url = new URL(window.location.href);
      url.searchParams.set('date', utcDateString);
      window.open(decodeURIComponent(url.toString()), '_parent');
    }
  }, [date, utcDateString, isPreview]);

  const value = useMemo(
    () => ({
      date,
      dateString,
      setDate,
      utcDateString,
    }),
    [date],
  );

  if (SSR) {
    return (
      <PreviewContext.Provider
        value={{
          date: new Date(),
          dateString: '',
          setDate: () => undefined,
          utcDateString: '',
        }}>
        {children}
      </PreviewContext.Provider>
    );
  }

  return <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>;
};

export default PreviewProvider;
